import {
  isInLicenseRenewalWindow,
  isWithinLicenseRenewalWindow,
  resolveLicenseExpirationDate,
  showLicenseActivationPanel,
} from "@/lib/licenseRenewal";
import { isBuyerLicenseCtaAudience } from "@/lib/accountStatus";
import { TRIAL_LICENSE_ACTIVATION_BODY } from "@/modules/panelHubOnboarding";
import {
  LEGAL_DOCUMENTOS_PATH,
  buildConfiguracionLicenciaUrl,
  buildConfiguracionRenovacionUrl,
} from "@/modules/config/configuracionRoutes";

export const PANEL_LICENSE_CTA_TITLE = "Tu licencia o periodo de prueba requiere atención";

export const PANEL_LICENSE_CTA_BODY = TRIAL_LICENSE_ACTIVATION_BODY;

export const PANEL_LICENSE_CTA_ACTIONS_TITLE = "Acciones disponibles";

export const LICENSE_CONTACT_ADMIN_MESSAGE =
  "Contacta al administrador del despacho para solicitar la activación.";

export const LICENSE_CTA_URLS = {
  solicitar: () => buildConfiguracionRenovacionUrl(),
  activar: () => buildConfiguracionLicenciaUrl({ activarLicencia: true }),
  archivoPdf: () => buildConfiguracionLicenciaUrl({ activarLicencia: true }),
  documentos: LEGAL_DOCUMENTOS_PATH,
};

/** @param {{ user?: object, systemInfo?: object, workspaceMode?: string, canSwitch?: boolean, buyerPreviewUx?: boolean }} ctx */
export function buildLicenseActivationPreviewCtx(ctx = {}) {
  if (ctx.buyerPreviewUx) {
    return { ...ctx, show: false, canRequest: false };
  }
  return ctx;
}

/** @param {{ user?: object, systemInfo?: object, workspaceMode?: string, canSwitch?: boolean, buyerPreviewUx?: boolean }} ctx */
export function shouldShowPanelLicenseCtaBlock(ctx) {
  return showLicenseActivationPanel(buildLicenseActivationPreviewCtx(ctx));
}

/**
 * UX de CTAs junto al aviso «requiere activación» (sin cambiar reglas de showLicenseActivationPanel).
 * @param {{ user?: object, systemInfo?: object, workspaceMode?: string, canSwitch?: boolean, buyerPreviewUx?: boolean }} ctx
 */
export function resolveLicenseActivationUx(ctx) {
  if (ctx.buyerPreviewUx) {
    return { show: false, canRequest: false, showContactMessage: false };
  }
  const effective = buildLicenseActivationPreviewCtx(ctx);
  const systemInfo = effective.systemInfo;
  if (!systemInfo || !effective.user) {
    return { show: false, canRequest: false, showContactMessage: false };
  }
  const sub = systemInfo.subscription || {};
  const trialExpired = sub.mode === "trial" && !sub.ok && sub.reason === "trial_expired";
  const showForBuyer = shouldShowPanelLicenseCtaBlock(effective);
  const audience = isBuyerLicenseCtaAudience(effective.user, systemInfo, {
    buyerPreviewUx: Boolean(effective.buyerPreviewUx),
  });
  const canRequest = audience && (showForBuyer || trialExpired);
  const show = showForBuyer || trialExpired;
  return {
    show,
    canRequest,
    showContactMessage: show && !canRequest,
  };
}

/** Etiquetas de botones según estado (mismas rutas/acciones existentes). */
export function resolveLicenseCtaLabels(systemInfo) {
  const expiration = resolveLicenseExpirationDate(systemInfo);
  const sub = systemInfo?.subscription || {};
  const inRenewal = isInLicenseRenewalWindow(expiration);
  const trialExpired = sub.mode === "trial" && !sub.ok && sub.reason === "trial_expired";
  if (trialExpired) {
    return {
      solicitar: "Solicitar licencia",
      activar: "Activar licencia recibida",
      archivoPdf: null,
      modulo: "Solicitar activación de módulo",
    };
  }
  const trialInWindow = sub.mode === "trial" && sub.ok && isWithinLicenseRenewalWindow(systemInfo);
  return {
    solicitar: inRenewal ? "Solicitar renovación" : "Solicitar licencia",
    activar: trialInWindow || sub.mode === "trial" ? "Registrar licencia" : "Activar licencia",
    archivoPdf: "Tengo archivo/PDF de licencia",
    modulo: "Solicitar activación de módulo",
  };
}

export function resolveSupportContactEmail(systemInfo) {
  const email = (systemInfo?.billing?.support_email || "").trim();
  return email || null;
}
